import React, { useEffect, useId, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { Link, useLocation } from "react-router-dom";
import { useLocalization } from "../localization";
import { usePrivacyConsent } from "./PrivacyConsentProvider";
import "./PrivacyConsentManager.css";

const PrivacyConsentManager = () => {
  const { t } = useLocalization();
  const location = useLocation();
  const mapReminderTitleId = useId();
  const {
    preferences,
    hasDecision,
    functionalAllowed,
    gpcEnabled,
    isPreferencesOpen,
    acceptAvailable,
    rejectOptional,
    saveChoices,
    allowFunctional,
    closePreferences,
    openPreferences,
  } = usePrivacyConsent();
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const bannerRef = useRef<HTMLElement | null>(null);
  const isMapCriticalRoute =
    location.pathname === "/discover" ||
    location.pathname === "/discover/map" ||
    location.pathname === "/live-racers" ||
    /^\/races\/[^/]+\/results\/map\/?$/.test(location.pathname) ||
    /^\/admin\/race-submissions\/[^/]+\/?$/.test(location.pathname);
  const showMapPermissionReminder =
    hasDecision && !functionalAllowed && isMapCriticalRoute;

  useEffect(() => {
    if (isPreferencesOpen) {
      setFunctional(preferences?.functional || false);
      setAnalytics(preferences?.analytics || false);
    }
  }, [isPreferencesOpen, preferences]);

  useEffect(() => {
    if (hasDecision || !bannerRef.current) {
      document.documentElement.style.removeProperty("--privacy-banner-offset");
      return undefined;
    }

    const banner = bannerRef.current;
    const updateOffset = () => {
      document.documentElement.style.setProperty(
        "--privacy-banner-offset",
        `${Math.ceil(banner.getBoundingClientRect().height + 36)}px`
      );
    };

    updateOffset();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateOffset)
        : null;
    resizeObserver?.observe(banner);
    window.addEventListener("resize", updateOffset);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateOffset);
      document.documentElement.style.removeProperty("--privacy-banner-offset");
    };
  }, [hasDecision]);

  return (
    <>
      {!hasDecision ? (
        <aside
          ref={bannerRef}
          className="privacy-banner"
          aria-labelledby="privacy-banner-title"
          aria-describedby="privacy-banner-description"
        >
          <div className="privacy-banner-copy">
            <p className="privacy-banner-eyebrow">{t("Your privacy")}</p>
            <h2 id="privacy-banner-title">{t("Privacy choices")}</h2>
            <p id="privacy-banner-description">
              {t(
                "We use required storage for sign-in, security, language, and your preferences. With your permission, we also load interactive maps and collect privacy-friendly usage analytics. We do not use advertising cookies."
              )}
            </p>
            <p className="privacy-banner-links">
              <Link to="/legal/cookies">{t("Cookie & Storage Policy")}</Link>
              <span aria-hidden="true">·</span>
              <Link to="/legal/privacy">{t("Privacy Policy")}</Link>
            </p>
            {gpcEnabled ? (
              <p className="privacy-gpc-notice" role="status">
                {t(
                  "Your Global Privacy Control signal is on. Advertising and data sharing remain off."
                )}
              </p>
            ) : null}
          </div>
          <div className="privacy-banner-actions">
            <Button variant="primary" onClick={rejectOptional}>
              {t("Reject non-essential")}
            </Button>
            <Button variant="outline-secondary" onClick={openPreferences}>
              {t("Manage preferences")}
            </Button>
            <Button variant="primary" onClick={acceptAvailable}>
              {t("Accept optional")}
            </Button>
          </div>
        </aside>
      ) : null}

      {showMapPermissionReminder ? (
        <aside
          className="map-permission-reminder"
          aria-labelledby={mapReminderTitleId}
        >
          <div className="map-permission-reminder-copy">
            <h2 id={mapReminderTitleId}>{t("Interactive map is off")}</h2>
            <p>
              {t(
                "Allow Mapbox to receive network and device data and store map-delivery identifiers before loading this map."
              )}
            </p>
          </div>
          <div className="map-permission-reminder-actions">
            <Button variant="primary" onClick={allowFunctional}>
              {t("Allow and load map")}
            </Button>
            <Button variant="outline-secondary" onClick={openPreferences}>
              {t("Review privacy choices")}
            </Button>
          </div>
        </aside>
      ) : null}

      <Modal
        show={isPreferencesOpen}
        onHide={closePreferences}
        centered
        restoreFocus
        className="privacy-preferences-modal"
        aria-labelledby="privacy-preferences-title"
      >
        <Modal.Header closeButton closeLabel={t("Close privacy preferences")}>
          <Modal.Title id="privacy-preferences-title">
            {t("Privacy preferences")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t(
              "Choose which optional technology RAUXA may use. You can change this choice at any time from the site footer."
            )}
          </p>

          <div className="privacy-choice-card">
            <div>
              <h3>{t("Required")}</h3>
              <p>
                {t(
                  "Needed for account access, security, payments, language, units, and remembering this choice."
                )}
              </p>
            </div>
            <span className="privacy-choice-status">{t("Always on")}</span>
          </div>

          <div className="privacy-choice-card">
            <div>
              <h3>{t("Interactive maps")}</h3>
              <p id="privacy-functional-description">
                {t(
                  "Allows Mapbox maps to load. Mapbox receives network and device data and may store identifiers for map delivery and billing."
                )}
              </p>
            </div>
            <Form.Check
              type="switch"
              id="privacy-functional-choice"
              label={t("Allow interactive maps")}
              checked={functional}
              onChange={(event) => setFunctional(event.target.checked)}
              aria-describedby="privacy-functional-description"
            />
          </div>

          <div className="privacy-choice-card">
            <div>
              <h3>{t("Usage analytics")}</h3>
              <p id="privacy-analytics-description">
                {t(
                  "Allows RAUXA to record page views and conversion actions with a temporary session identifier. We do not use this for advertising."
                )}
              </p>
            </div>
            <Form.Check
              type="switch"
              id="privacy-analytics-choice"
              label={t("Allow usage analytics")}
              checked={analytics}
              onChange={(event) => setAnalytics(event.target.checked)}
              aria-describedby="privacy-analytics-description"
            />
          </div>

          <div className="privacy-choice-card privacy-choice-card-disabled">
            <div>
              <h3>{t("Advertising")}</h3>
              <p>{t("RAUXA does not load advertising cookies or behavioral advertising tools.")}</p>
            </div>
            <span className="privacy-choice-status">{t("Off")}</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={rejectOptional}>
            {t("Reject non-essential")}
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              saveChoices({
                functional,
                analytics,
                advertising: false,
              })
            }
          >
            {t("Save preferences")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PrivacyConsentManager;
